import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Term from "./Term";
import { sourceStatusTerm } from "./glossary";
import type { ProgramSourceStatus } from "@/services/adminProgramNews";

export const LEGEND_STORAGE_KEY = "programNewsSources.legendCollapsed";

interface SourceStatusLegendProps {
  /** False outside QA: the fix sentence stops at "Fixes are made in QA." */
  writable: boolean;
}

const ENTRIES: Array<{ status: ProgramSourceStatus; label: string; text: string }> = [
  {
    status: "verified",
    label: "Verified",
    text: "We found this program's news feed and it returned golf stories. Nothing to do.",
  },
  {
    status: "queued",
    label: "Queued",
    text: "We found the school's athletics site but no golf news feed we recognize. Usually a platform we don't support yet. Set an override only if you know the feed's page.",
  },
  {
    status: "failed",
    label: "Failed",
    text: "We couldn't find an athletics website for this college at all. Set an override with the athletics site's home page (for example https://gofrogs.com/) and we'll probe it for a golf feed.",
  },
  {
    status: "override_stale",
    label: "Override stale",
    text: "Someone set a URL for this program and it stopped working; the crawl found a possible replacement. Review the suggestion.",
  },
  {
    status: "disabled",
    label: "Disabled",
    text: "Turned off on purpose. Not collected. Disable a source that will never have golf news; it stops showing as failed.",
  },
];

const ORDER_SENTENCE = "Rows on an athlete's list come first.";
const FIX_SENTENCE_WRITABLE =
  "Fixes are made in QA and reach staging and production on the next data push.";
const FIX_SENTENCE_READ_ONLY = "Fixes are made in QA.";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(LEGEND_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) localStorage.setItem(LEGEND_STORAGE_KEY, "1");
    else localStorage.removeItem(LEGEND_STORAGE_KEY);
  } catch {
    // Remembering the state is a convenience; ignore storage failures.
  }
}

const SourceStatusLegend = ({ writable }: SourceStatusLegendProps) => {
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);

  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
  }

  return (
    <section className="rounded-md border bg-muted/30 p-3 text-sm" aria-label="Status legend">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-medium">What the statuses mean</h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          aria-expanded={!collapsed}
          onClick={toggle}
        >
          {collapsed ? (
            <>
              <ChevronRight className="mr-1 h-3 w-3" />
              Show
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 h-3 w-3" />
              Hide
            </>
          )}
        </Button>
      </div>
      {collapsed ? null : (
        <div className="mt-2 space-y-2">
          <dl className="space-y-1">
            {ENTRIES.map(({ status, label, text }) => (
              <div key={status} className="flex flex-col sm:flex-row sm:gap-2">
                <dt className="w-32 shrink-0 font-medium">
                  <Term term={sourceStatusTerm(status)}>{label}</Term>
                </dt>
                <dd className="text-muted-foreground">{text}</dd>
              </div>
            ))}
          </dl>
          <p className="text-muted-foreground">
            {`${ORDER_SENTENCE} ${writable ? FIX_SENTENCE_WRITABLE : FIX_SENTENCE_READ_ONLY}`}
          </p>
        </div>
      )}
    </section>
  );
};

export default SourceStatusLegend;
